import { useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Award, Lock } from "lucide-react";
import { motion } from "framer-motion";

interface DiaryEntry {
  id: string;
  meal_type: string;
  date: string;
  calories: number;
  photo_path?: string;
  photo_url?: string;
}

export interface Achievement {
  id: string;
  emoji: string;
  title: string;
  description: string;
  check: (ctx: AchievementContext) => boolean;
}

export interface AchievementContext {
  totalEntries: number;
  streak: number;
  uniqueDays: number;
  daysOnTarget: number;
  consecutiveDaysOnTarget: number;
  mealTypesUsed: Set<string>;
  hasPhoto: boolean;
  entriesWithPhoto: number;
  // Metrics
  totalMetricEntries: number;
  metricsWithPhotos: number;
  measuredBodyParts: number;
  weightVariation: boolean;
  // Water
  totalWaterDays: number;
  waterOnTargetDays: number;
  consecutiveWaterDays: number;
  waterStreak: number;
}

export const achievements: Achievement[] = [
  // === Primeiros passos ===
  {
    id: "first_entry",
    emoji: "🌱",
    title: "Primeiro Passo",
    description: "Registre sua primeira refeição",
    check: (ctx) => ctx.totalEntries >= 1,
  },
  {
    id: "getting_started",
    emoji: "🚀",
    title: "Decolando",
    description: "Registre refeições em 3 dias diferentes",
    check: (ctx) => ctx.uniqueDays >= 3,
  },

  // === Sequências ===
  {
    id: "streak_3",
    emoji: "🔥",
    title: "Sequência de 3",
    description: "Registre refeições por 3 dias seguidos",
    check: (ctx) => ctx.streak >= 3,
  },
  {
    id: "streak_7",
    emoji: "⚡",
    title: "Semana Perfeita",
    description: "Registre refeições por 7 dias seguidos",
    check: (ctx) => ctx.streak >= 7,
  },
  {
    id: "streak_14",
    emoji: "💪",
    title: "Duas Semanas",
    description: "Sequência de 14 dias consecutivos",
    check: (ctx) => ctx.streak >= 14,
  },
  {
    id: "streak_30",
    emoji: "👑",
    title: "Mês Completo",
    description: "Sequência de 30 dias consecutivos",
    check: (ctx) => ctx.streak >= 30,
  },
  {
    id: "streak_60",
    emoji: "🦁",
    title: "Inabalável",
    description: "Sequência de 60 dias consecutivos",
    check: (ctx) => ctx.streak >= 60,
  },
  {
    id: "streak_90",
    emoji: "💎",
    title: "Diamante",
    description: "Sequência de 90 dias consecutivos",
    check: (ctx) => ctx.streak >= 90,
  },

  // === Volume de refeições ===
  {
    id: "meals_10",
    emoji: "🍽️",
    title: "10 Refeições",
    description: "Registre 10 refeições no total",
    check: (ctx) => ctx.totalEntries >= 10,
  },
  {
    id: "meals_25",
    emoji: "🥄",
    title: "25 Refeições",
    description: "Registre 25 refeições no total",
    check: (ctx) => ctx.totalEntries >= 25,
  },
  {
    id: "meals_50",
    emoji: "🏅",
    title: "50 Refeições",
    description: "Registre 50 refeições no total",
    check: (ctx) => ctx.totalEntries >= 50,
  },
  {
    id: "meals_100",
    emoji: "🏆",
    title: "Centenário",
    description: "Registre 100 refeições no total",
    check: (ctx) => ctx.totalEntries >= 100,
  },
  {
    id: "meals_250",
    emoji: "🔱",
    title: "Veterano",
    description: "Registre 250 refeições no total",
    check: (ctx) => ctx.totalEntries >= 250,
  },
  {
    id: "meals_500",
    emoji: "🌟",
    title: "Lendário",
    description: "Registre 500 refeições no total",
    check: (ctx) => ctx.totalEntries >= 500,
  },

  // === Meta calórica ===
  {
    id: "on_target",
    emoji: "🎯",
    title: "Na Meta",
    description: "Atinja a meta calórica em um dia",
    check: (ctx) => ctx.daysOnTarget >= 1,
  },
  {
    id: "on_target_3",
    emoji: "🏹",
    title: "Foco Total",
    description: "Atinja a meta calórica por 3 dias seguidos",
    check: (ctx) => ctx.consecutiveDaysOnTarget >= 3,
  },
  {
    id: "on_target_7",
    emoji: "⭐",
    title: "Semana na Meta",
    description: "Atinja a meta calórica por 7 dias seguidos",
    check: (ctx) => ctx.consecutiveDaysOnTarget >= 7,
  },
  {
    id: "on_target_14",
    emoji: "🌙",
    title: "Consistência",
    description: "Atinja a meta calórica por 14 dias seguidos",
    check: (ctx) => ctx.consecutiveDaysOnTarget >= 14,
  },
  {
    id: "on_target_30",
    emoji: "☀️",
    title: "Mestre da Meta",
    description: "Atinja a meta calórica por 30 dias seguidos",
    check: (ctx) => ctx.consecutiveDaysOnTarget >= 30,
  },

  // === Dias ativos ===
  {
    id: "days_7",
    emoji: "📅",
    title: "Uma Semana",
    description: "Registre refeições em 7 dias diferentes",
    check: (ctx) => ctx.uniqueDays >= 7,
  },
  {
    id: "days_30",
    emoji: "📆",
    title: "Um Mês Ativo",
    description: "Registre refeições em 30 dias diferentes",
    check: (ctx) => ctx.uniqueDays >= 30,
  },
  {
    id: "days_100",
    emoji: "🗓️",
    title: "100 Dias",
    description: "Registre refeições em 100 dias diferentes",
    check: (ctx) => ctx.uniqueDays >= 100,
  },

  // === Variedade & fotos ===
  {
    id: "all_meals",
    emoji: "🌈",
    title: "Cardápio Completo",
    description: "Registre todos os tipos de refeição",
    check: (ctx) => ctx.mealTypesUsed.size >= 5,
  },
  {
    id: "photographer",
    emoji: "📸",
    title: "Fotógrafo",
    description: "Registre uma refeição com foto",
    check: (ctx) => ctx.hasPhoto,
  },
  {
    id: "photos_10",
    emoji: "🖼️",
    title: "Álbum de Fotos",
    description: "Registre 10 refeições com foto",
    check: (ctx) => ctx.entriesWithPhoto >= 10,
  },
  {
    id: "photos_50",
    emoji: "📷",
    title: "Fotógrafo Pro",
    description: "Registre 50 refeições com foto",
    check: (ctx) => ctx.entriesWithPhoto >= 50,
  },

  // === Métricas Corporais ===
  {
    id: "first_metric",
    emoji: "📏",
    title: "Primeira Medida",
    description: "Registre sua primeira métrica corporal",
    check: (ctx) => ctx.totalMetricEntries >= 1,
  },
  {
    id: "metrics_5",
    emoji: "📐",
    title: "Acompanhamento",
    description: "Registre 5 métricas corporais",
    check: (ctx) => ctx.totalMetricEntries >= 5,
  },
  {
    id: "metrics_12",
    emoji: "🧮",
    title: "Monitoramento Anual",
    description: "Registre 12 métricas corporais",
    check: (ctx) => ctx.totalMetricEntries >= 12,
  },
  {
    id: "metrics_photo",
    emoji: "🪞",
    title: "Registro Visual",
    description: "Registre uma métrica com foto de progresso",
    check: (ctx) => ctx.metricsWithPhotos >= 1,
  },
  {
    id: "metrics_photos_5",
    emoji: "🎞️",
    title: "Evolução em Fotos",
    description: "Registre 5 métricas com fotos de progresso",
    check: (ctx) => ctx.metricsWithPhotos >= 5,
  },
  {
    id: "full_body_check",
    emoji: "🫀",
    title: "Check-up Completo",
    description: "Preencha todas as circunferências em um registro",
    check: (ctx) => ctx.measuredBodyParts >= 6,
  },
  {
    id: "weight_tracking",
    emoji: "⚖️",
    title: "Balança em Dia",
    description: "Registre pelo menos 2 pesagens para acompanhar evolução",
    check: (ctx) => ctx.weightVariation,
  },

  // === Hidratação ===
  {
    id: "first_water",
    emoji: "💧",
    title: "Primeira Gota",
    description: "Registre sua ingestão de água pela primeira vez",
    check: (ctx) => ctx.totalWaterDays >= 1,
  },
  {
    id: "water_target",
    emoji: "🚰",
    title: "Meta Hídrica",
    description: "Atinja a meta de água em um dia",
    check: (ctx) => ctx.waterOnTargetDays >= 1,
  },
  {
    id: "water_streak_3",
    emoji: "🌊",
    title: "Fluxo Constante",
    description: "Atinja a meta de água por 3 dias seguidos",
    check: (ctx) => ctx.consecutiveWaterDays >= 3,
  },
  {
    id: "water_streak_7",
    emoji: "🏊",
    title: "Semana Hidratada",
    description: "Atinja a meta de água por 7 dias seguidos",
    check: (ctx) => ctx.consecutiveWaterDays >= 7,
  },
  {
    id: "water_streak_30",
    emoji: "🐬",
    title: "Mestre da Hidratação",
    description: "Atinja a meta de água por 30 dias seguidos",
    check: (ctx) => ctx.consecutiveWaterDays >= 30,
  },
  {
    id: "water_days_30",
    emoji: "💦",
    title: "Hábito de Ouro",
    description: "Registre água em 30 dias diferentes",
    check: (ctx) => ctx.totalWaterDays >= 30,
  },
];

interface AchievementsSheetProps {
  entries?: DiaryEntry[];
  streak?: number;
  targetCalories?: number;
  precomputedContext?: AchievementContext;
  children: React.ReactNode;
}

export function AchievementsSheet({ entries, streak, targetCalories, precomputedContext, children }: AchievementsSheetProps) {
  const [open, setOpen] = useState(false);

  const context = useMemo<AchievementContext>(() => {
    if (precomputedContext) return precomputedContext;

    const safeEntries = entries || [];
    const safeStreak = streak || 0;
    const safeTarget = targetCalories || 2000;

    const uniqueDays = new Set(safeEntries.map(e => e.date));
    const mealTypesUsed = new Set(safeEntries.map(e => e.meal_type));
    const hasPhoto = safeEntries.some(e => e.photo_path || e.photo_url);

    const caloriesByDay: Record<string, number> = {};
    safeEntries.forEach(e => {
      caloriesByDay[e.date] = (caloriesByDay[e.date] || 0) + Number(e.calories);
    });
    const daysOnTarget = Object.values(caloriesByDay).filter(cal => {
      const ratio = cal / safeTarget;
      return ratio >= 0.8 && ratio <= 1.1;
    }).length;

    return {
      totalEntries: safeEntries.length,
      streak: safeStreak,
      uniqueDays: uniqueDays.size,
      daysOnTarget,
      consecutiveDaysOnTarget: 0,
      mealTypesUsed,
      hasPhoto,
      entriesWithPhoto: safeEntries.filter(e => e.photo_path || e.photo_url).length,
      totalMetricEntries: 0,
      metricsWithPhotos: 0,
      measuredBodyParts: 0,
      weightVariation: false,
      totalWaterDays: 0,
      waterOnTargetDays: 0,
      consecutiveWaterDays: 0,
      waterStreak: 0,
    };
  }, [entries, streak, targetCalories, precomputedContext]);

  const unlocked = useMemo(() => achievements.filter(a => a.check(context)), [context]);
  const unlockedCount = unlocked.length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {children}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <Award className="w-5 h-5 text-primary" />
            Conquistas
          </SheetTitle>
          <p className="text-sm text-muted-foreground">
            {unlockedCount} de {achievements.length} desbloqueadas
          </p>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden mt-2">
            <motion.div
              className="h-full rounded-full gradient-primary"
              initial={{ width: 0 }}
              animate={{ width: `${(unlockedCount / achievements.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </SheetHeader>

        <div className="space-y-3">
          {achievements.map((achievement, i) => {
            const isUnlocked = achievement.check(context);
            return (
              <motion.div
                key={achievement.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  isUnlocked
                    ? "border-primary/30 bg-primary/5"
                    : "border-border bg-muted/30 opacity-60"
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${
                  isUnlocked ? "bg-primary/10" : "bg-muted"
                }`}>
                  {isUnlocked ? achievement.emoji : <Lock className="w-4 h-4 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${isUnlocked ? "text-foreground" : "text-muted-foreground"}`}>
                    {achievement.title}
                  </p>
                  <p className="text-xs text-muted-foreground">{achievement.description}</p>
                </div>
                {isUnlocked && (
                  <div className="w-6 h-6 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-foreground text-xs">✓</span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export const TOTAL_ACHIEVEMENTS = achievements.length;
